import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Check } from "lucide-react";
import { motion } from "framer-motion";
import Seo from "@/components/Seo";
import PageHeader from "@/components/PageHeader";
import { Overline } from "@/components/Cards";
import { Button } from "@/components/ui/button";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";

const TIERS = [
  { name: "Artist", price: 15, tag: "For makers & creatives", color: "#00E5FF", features: ["Artist studio profile", "Post to First Fridays", "Sell via RiNo Made", "Listed in directory"] },
  { name: "Free", price: 0, tag: "Get on the map", color: "#A3A3A3", features: ["Basic directory listing", "1 photo", "Business info & hours", "Appear in search"] },
  { name: "Pro", price: 29, tag: "Most popular", color: "#FF0055", featured: true, features: ["Everything in Free", "Photo gallery", "Post unlimited events", "Post offers & deals", "Join promotions ($5 Wed)", "Monthly performance email"] },
  { name: "Premium", price: 99, tag: "Maximum reach", color: "#FFEA00", features: ["Everything in Pro", "Featured placement", "Homepage spotlight", "Advertising slots", "Priority Ranger coverage", "Dedicated account support"] },
];

export default function Join() {
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: "", email: "", business: "", tier: "Pro" });
  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/subscribe", { email: form.email, name: form.name });
      toast.success("Great! Create your account to claim your listing.");
      navigate("/owner/login");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  return (
    <div>
      <Seo title="Join Visit RiNo" description="Become a member of Visit RiNo. Choose a membership tier for your business or art practice and get discovered across Denver's River North Art District." />
      <PageHeader overline="Membership" title="Join the District" subtitle="Get your business or art practice in front of everyone exploring RiNo. Pick a tier and start showing up." />

      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
        {TIERS.map((t, i) => (
          <motion.div key={t.name} initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.06 }}
            data-testid={`tier-${t.name.toLowerCase()}`}
            className={`relative border bg-[#141414] p-7 flex flex-col ${t.featured ? "border-[#FF0055]" : "border-white/10"}`}>
            {t.featured && <span className="absolute -top-3 left-7 bg-[#FF0055] text-white text-[10px] font-accent px-3 py-1">{t.tag}</span>}
            <span className="font-accent text-sm" style={{ color: t.color }}>{t.name}</span>
            <div className="mt-3 flex items-end gap-1">
              <span className="font-display font-black text-4xl text-white">${t.price}</span>
              <span className="text-neutral-500 text-sm mb-1">/mo</span>
            </div>
            {!t.featured && <p className="text-neutral-500 text-xs mt-1">{t.tag}</p>}
            <ul className="mt-6 space-y-3 flex-1">
              {t.features.map((f) => (
                <li key={f} className="flex items-start gap-2 text-neutral-300 text-sm"><Check size={15} className="mt-0.5" style={{ color: t.color }} /> {f}</li>
              ))}
            </ul>
            <button onClick={() => setForm({ ...form, tier: t.name })} data-testid={`select-tier-${t.name.toLowerCase()}`}
              className={`mt-7 py-2.5 text-sm font-semibold transition-colors ${form.tier === t.name ? "bg-[#FF0055] text-white" : "border border-white/20 text-white hover:bg-white hover:text-black"}`}>
              {form.tier === t.name ? "Selected" : "Choose"}
            </button>
          </motion.div>
        ))}
      </section>

      <section className="max-w-3xl mx-auto px-4 sm:px-6 py-12">
        <div className="border border-white/10 bg-[#141414] p-8">
          <Overline>Get Started</Overline>
          <h2 className="font-display font-extrabold text-2xl text-white mt-2">Claim your listing on the <span className="text-[#FF0055]">{form.tier}</span> tier</h2>
          <p className="text-neutral-500 text-sm mt-2">Create your free business account, publish your listing instantly, then upgrade to Pro or Premium with secure Stripe checkout.</p>
          <form onSubmit={submit} className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-4">
            <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Your name" data-testid="join-name" className="bg-transparent border border-white/15 px-3 py-2.5 text-sm text-white outline-none focus:border-[#00E5FF]" />
            <input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="Email" data-testid="join-email" className="bg-transparent border border-white/15 px-3 py-2.5 text-sm text-white outline-none focus:border-[#00E5FF]" />
            <input value={form.business} onChange={(e) => setForm({ ...form, business: e.target.value })} placeholder="Business / practice name" data-testid="join-business" className="sm:col-span-2 bg-transparent border border-white/15 px-3 py-2.5 text-sm text-white outline-none focus:border-[#00E5FF]" />
            <Button type="submit" data-testid="join-submit" className="sm:col-span-2 bg-[#FF0055] hover:bg-[#ff2d74] rounded-none h-11 font-semibold">Create Business Account</Button>
          </form>
        </div>
      </section>
    </div>
  );
}
